import { Shield, CheckCircle2, XCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/contexts/AuthContext';
import EnhancedAppHeader from '@/components/EnhancedAppHeader';

export default function MyRole() {
  const { user, activeOrg, userRole } = useAuth();

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'org_admin': return 'bg-primary text-primary-foreground';
      case 'admin': return 'bg-blue-600 text-white';
      case 'planner': return 'bg-accent text-accent-foreground';
      case 'client': return 'bg-secondary text-secondary-foreground';
      case 'read_only_client': return 'bg-secondary text-secondary-foreground'; // Legacy support
      default: return 'bg-muted';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'org_admin': return 'Organization Admin';
      case 'admin': return 'Admin';
      case 'planner': return 'Planner';
      case 'client': return 'Client';
      case 'read_only_client': return 'Client (Read-only)'; // Legacy support
      default: return role;
    }
  };

  const getRoleDescription = (role) => {
    switch (role) {
      case 'org_admin':
        return 'You have full administrative access. You can manage team members, organization settings, weddings, and all configurations. You own the organization.';
      case 'admin':
        return 'You have administrative access to all weddings and can manage team members (except org admins). You can create and manage weddings, but cannot modify critical organization settings.';
      case 'planner':
        return 'You can access and update events, tasks, vendors, budget, menu, and guest details for weddings you\'re assigned to. You cannot create or delete weddings.';
      case 'client':
      case 'read_only_client': // Legacy support
        return 'You can view wedding planning progress, timeline, guest lists, and vendor details for assigned weddings. You have limited editing permissions for guest information when enabled by your planner.';
      default:
        return '';
    }
  };

  const getPermissions = (role) => {
    switch (role) {
      case 'org_admin':
        return [
          { label: 'Create and delete weddings', allowed: true },
          { label: 'Edit all wedding details', allowed: true },
          { label: 'Invite and manage team members', allowed: true },
          { label: 'Change member roles (including org_admin)', allowed: true },
          { label: 'Manage organization settings', allowed: true },
          { label: 'Access billing and account', allowed: true },
          { label: 'View all analytics and reports', allowed: true },
          { label: 'Override any permission', allowed: true }
        ];
      case 'admin':
        return [
          { label: 'Create and delete weddings', allowed: true },
          { label: 'Edit all wedding details', allowed: true },
          { label: 'Invite and manage team members', allowed: true },
          { label: 'Change member roles (except org_admin)', allowed: true },
          { label: 'View organization settings', allowed: true },
          { label: 'Manage all weddings in organization', allowed: true },
          { label: 'Cannot modify subscription/billing', allowed: false },
          { label: 'Cannot delete organization', allowed: false }
        ];
      case 'planner':
        return [
          { label: 'Create and delete weddings', allowed: false },
          { label: 'Edit assigned wedding details', allowed: true },
          { label: 'Manage events and timeline', allowed: true },
          { label: 'Update vendors and budget', allowed: true },
          { label: 'Manage guest lists', allowed: true },
          { label: 'Manage tasks and menu', allowed: true },
          { label: 'Invite team members', allowed: false },
          { label: 'Access organization settings', allowed: false }
        ];
      case 'client':
      case 'read_only_client': // Legacy support
        return [
          { label: 'View assigned weddings', allowed: true },
          { label: 'View event timeline', allowed: true },
          { label: 'View guest lists', allowed: true },
          { label: 'View vendor details', allowed: true },
          { label: 'View budget information', allowed: true },
          { label: 'Edit guest information (when enabled)', allowed: true },
          { label: 'Edit other information', allowed: false },
          { label: 'Access team or org settings', allowed: false }
        ];
      default:
        return [];
    }
  };

  const permissions = getPermissions(userRole);

  return (
    <div className="min-h-screen gradient-soft pb-12">
      <EnhancedAppHeader 
        title="My Role & Access"
        subtitle="Your permissions and access level in this organization"
      />

      <div className="container mx-auto px-4 py-6">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Current Role Card */}
          <Card className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-full gradient-primary">
                <Shield className="h-8 w-8 text-primary-foreground" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-2">Your Role</h2>
                <Badge className={`${getRoleBadgeColor(userRole)} mb-3 text-base px-3 py-1`}>
                  {getRoleLabel(userRole)}
                </Badge>
                <p className="text-muted-foreground mb-4">
                  {getRoleDescription(userRole)}
                </p>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm">
                    <strong>Organization:</strong> {activeOrg?.name}
                  </p>
                  <p className="text-sm mt-1">
                    <strong>Your Name:</strong> {user?.name}
                  </p>
                  <p className="text-sm mt-1">
                    <strong>Email:</strong> {user?.email}
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Permissions List */}
          <Card className="p-6">
            <h3 className="text-xl font-bold mb-4">What You Can Do</h3>
            <div className="space-y-3">
              {permissions.map((perm, index) => (
                <div 
                  key={index} 
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  {perm.allowed ? (
                    <CheckCircle2 className="h-5 w-5 text-success shrink-0" />
                  ) : (
                    <XCircle className="h-5 w-5 text-muted-foreground shrink-0" />
                  )}
                  <span className={perm.allowed ? 'text-foreground' : 'text-muted-foreground'}>
                    {perm.label}
                  </span>
                </div>
              ))}
            </div>
          </Card>

          {/* Help Section */}
          <Alert className="border-primary/20 bg-primary/5">
            <AlertDescription>
              <p className="text-sm">
                <strong>Need different access?</strong> Contact an organization admin to change your role or request additional permissions.
              </p>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    </div>
  );
}